import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import ActiveAlert, { ActiveAlertTableRow } from "../types/activeAlert";
import SiteAlert from "../types/siteAlert";
import fetchWithDeduplication from "../helpers/fetchWithDeduplication";

interface AlertState {
  status: "idle" | "loading" | "failed";
  siteAlertStatus: "idle" | "loading" | "failed";
  activeAlerts?: ActiveAlertTableRow[];
  siteAlerts?: SiteAlert[];
}

const initialState: AlertState = {
  status: "idle",
  siteAlertStatus: "idle",
};

export const fetchAlerts = createAsyncThunk<ActiveAlertTableRow[]>(
  "alerts/fetch-all",
  async () => {
    const response = await http.get<ActiveAlertTableRow[]>("/alert");
    return response.data;
  },
);

export const fetchAlertDetails = createAsyncThunk<ActiveAlert, number>(
  "alerts/fetch",
  async (id: number) => {
    const response = await http.get<ActiveAlert>(`/alert/${id}`);
    return response.data;
  },
);

export const disableAlert = createAsyncThunk<null, number>(
  "alerts/disable",
  async (id: number) => {
    const response = await http.delete(`/alert/${id}`, {
      params: {
        successToast: true,
      },
    });
    return response.data;
  },
);

export const fetchSiteAlerts = createAsyncThunk<SiteAlert[]>(
  "alerts/fetch-site",
  async () => {
    const key = `/alert/site`;
    const response = await fetchWithDeduplication(key, () =>
      http.get<SiteAlert[]>(key),
    );

    return response.data;
  },
);

const getReadAlertsFromStorage = (): string[] => {
  const data = localStorage.getItem("readAlerts");
  return data ? JSON.parse(data) : [];
};

const saveReadAlertsToStorage = (readIds: string[]) => {
  localStorage.setItem("readAlerts", JSON.stringify(readIds));
};

const alertSlice = createSlice({
  name: "alert",
  initialState,
  reducers: {
    clearAlertList(state) {
      state.activeAlerts = initialState.activeAlerts;
    },
    markAlertAsRead: (state, action) => {
      const alertId = action.payload;
      const existing = getReadAlertsFromStorage();
      if (!existing.includes(alertId)) {
        const updated = [...existing, alertId];
        saveReadAlertsToStorage(updated);
      }

      const alert = state.siteAlerts?.find(
        (a) => a.id === alertId || a.stringId === alertId,
      );
      if (alert) {
        alert.read = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlerts.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        state.status = "idle";
        state.activeAlerts = action.payload;
      })
      .addCase(fetchAlerts.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(disableAlert.pending, (state) => {
        state.status = "loading";
      })
      .addCase(disableAlert.fulfilled, (state, action) => {
        state.status = "idle";
      })
      .addCase(disableAlert.rejected, (state, action) => {
        state.status = "failed";
      })
      .addCase(fetchSiteAlerts.pending, (state) => {
        state.siteAlertStatus = "loading";
      })
      .addCase(fetchSiteAlerts.fulfilled, (state, action) => {
        const readIds = getReadAlertsFromStorage();
        state.siteAlerts = action.payload.map((alert) => ({
          ...alert,
          read: alert.stringId
            ? readIds.includes(alert.stringId)
            : readIds.includes(alert.id.toString()),
        }));
        state.siteAlertStatus = "idle";
      })
      .addCase(fetchSiteAlerts.rejected, (state, action) => {
        state.siteAlertStatus = "failed";
      });
  },
});

export const { clearAlertList, markAlertAsRead } = alertSlice.actions;

export default alertSlice.reducer;
